import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createSherexFastener,
  deleteSherexFastener,
  getSherexFastener,
  listSherexFasteners,
  listSherexFastenerOptions,
  updateSherexFastener,
  type CreateSherexFastenerBody,
  type ListSherexFastenersParams,
  type PaginatedSherexFasteners,
  type SherexFastener,
  type SherexFastenerOptions,
  type UpdateSherexFastenerBody,
} from "../api/sherexFasteners";

export function useSherexFasteners(params: ListSherexFastenersParams) {
  return useQuery<PaginatedSherexFasteners>({
    queryKey: ["sherex-fasteners", "list", params],
    queryFn: () => listSherexFasteners(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useSherexFastener(partNumber: string | null) {
  return useQuery<SherexFastener>({
    queryKey: ["sherex-fasteners", "detail", partNumber],
    queryFn: () => getSherexFastener(partNumber as string),
    enabled: !!partNumber,
    staleTime: 30_000,
  });
}

export function useSherexFastenerOptions() {
  return useQuery<SherexFastenerOptions>({
    queryKey: ["sherex-fasteners", "options"],
    queryFn: () => listSherexFastenerOptions(),
    staleTime: 5 * 60_000,
  });
}

export function useCreateSherexFastener() {
  const queryClient = useQueryClient();
  return useMutation<SherexFastener, Error, CreateSherexFastenerBody>({
    mutationFn: (body) => createSherexFastener(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["sherex-fasteners"] });
    },
  });
}

export function useUpdateSherexFastener() {
  const queryClient = useQueryClient();
  return useMutation<
    SherexFastener,
    Error,
    { partNumber: string; body: UpdateSherexFastenerBody }
  >({
    mutationFn: ({ partNumber, body }) => updateSherexFastener(partNumber, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["sherex-fasteners"] });
    },
  });
}

export function useDeleteSherexFastener() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (partNumber) => deleteSherexFastener(partNumber),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["sherex-fasteners"] });
    },
  });
}
